import {
  CAPTURE_REPLAY_ORGANIZATION_IDS,
  isNorthstarCaptureReplayOrganization,
  NORTHSTAR_CAPTURE_REPLAY_ORGANIZATION,
} from '../Environment';

export const ONGOING_ASSESSMENT_REPLAY_VERSION = 'northstar-ongoing-assessment-v1';
export const ONGOING_ASSESSMENT_APPROVED_NOTE = 'Completed eight scheduled inspections with timely documentation. One ambiguous safety condition required supervisor follow-up before it was escalated.';

export type OngoingAssessmentCriterionKind = 'Ability' | 'Behavior' | 'Counterproductive behavior';

export interface OngoingAssessmentReplayCriterion {
  Id: string;
  Group: string;
  Kind: OngoingAssessmentCriterionKind;
  Name: string;
  Rating: string;
  Note: string;
  RatingOptions: readonly string[];
}

export interface OngoingAssessmentReplay {
  Version: string;
  IsSynthetic: true;
  ReadOnly: true;
  OrganizationId: string;
  OrganizationName: string;
  TalentName: string;
  PositionName: string;
  TemplateName: string;
  Events: readonly { Date: string; Title: string; Description: string }[];
  PriorAssessment: { Date: string; Summary: string };
  ActiveGoal: { Title: string; Narrative: string; Status: 'Active' };
  Criteria: readonly OngoingAssessmentReplayCriterion[];
  SupervisorNote: string;
}

const deepFreeze = <T,>(value: T): T => {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value as Record<string, unknown>).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
};

const ABILITY_OPTIONS = deepFreeze(['Unskilled', 'Beginner', 'Intermediate', 'Advanced', 'Expert']);
const BEHAVIOR_OPTIONS = deepFreeze(['Never', 'Rarely', 'Sometimes', 'Usually', 'Always']);

export const ONGOING_ASSESSMENT_REPLAY_CANONICAL: Omit<OngoingAssessmentReplay, 'OrganizationId'> = deepFreeze({
  Version: ONGOING_ASSESSMENT_REPLAY_VERSION,
  IsSynthetic: true,
  ReadOnly: true,
  OrganizationName: NORTHSTAR_CAPTURE_REPLAY_ORGANIZATION,
  TalentName: 'Jordan Avery',
  PositionName: 'Field Service Technician',
  TemplateName: 'Field Service Technician Success Profile',
  Events: [
    {
      Date: 'July 12, 2026',
      Title: 'Completed eight scheduled inspections',
      Description: 'Jordan completed all eight scheduled equipment inspections, recorded the service findings, and identified the maintenance actions needed for each unit.',
    },
    {
      Date: 'July 28, 2026',
      Title: 'Submitted service records on time',
      Description: 'Jordan submitted the inspection and preventive-maintenance records on time, with clear equipment conditions and completed actions.',
    },
  ],
  PriorAssessment: {
    Date: 'April 15, 2026',
    Summary: 'Jordan completed scheduled inspections and documented findings promptly. More consistent escalation of ambiguous safety conditions would strengthen the work.',
  },
  ActiveGoal: {
    Title: 'Use the safety-escalation checklist consistently',
    Narrative: 'Use the safety-escalation checklist whenever a condition is ambiguous, and review the record with Northstar Trainer each week for 30 days.',
    Status: 'Active',
  },
  Criteria: [
    {
      Id: 'inspect-equipment', Group: 'Performant', Kind: 'Ability',
      Name: 'Inspects commercial equipment systematically', Rating: 'Advanced',
      Note: 'Completed eight scheduled inspections — July 12, 2026.', RatingOptions: ABILITY_OPTIONS,
    },
    {
      Id: 'preventive-maintenance', Group: 'Sustainable', Kind: 'Ability',
      Name: 'Performs preventive maintenance safely', Rating: 'Advanced', Note: '', RatingOptions: ABILITY_OPTIONS,
    },
    {
      Id: 'documents-findings', Group: 'Communication', Kind: 'Behavior',
      Name: 'Documents service findings promptly', Rating: 'Usually',
      Note: 'Submitted service records on time — July 28, 2026.', RatingOptions: BEHAVIOR_OPTIONS,
    },
    {
      Id: 'escalates-risks', Group: 'Communication', Kind: 'Behavior',
      Name: 'Escalates ambiguous safety risks before continuing work', Rating: 'Sometimes',
      Note: 'Ambiguous safety condition required follow-up — August 6, 2026.', RatingOptions: BEHAVIOR_OPTIONS,
    },
    {
      Id: 'shares-knowledge', Group: 'Culture', Kind: 'Behavior',
      Name: 'Shares field knowledge with teammates', Rating: 'Usually', Note: '', RatingOptions: BEHAVIOR_OPTIONS,
    },
    {
      Id: 'proceeds-when-unclear', Group: 'Sustainable', Kind: 'Counterproductive behavior',
      Name: 'Proceeds when safety conditions are unclear', Rating: 'Rarely',
      Note: 'Lower frequency is better for this counterproductive behavior.', RatingOptions: BEHAVIOR_OPTIONS,
    },
  ],
  SupervisorNote: '',
});

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

export const ongoingAssessmentReplayFingerprint = (replay: OngoingAssessmentReplay): string =>
  JSON.stringify({ ...replay, OrganizationId: '' });

export const expectedOngoingAssessmentReplayFingerprint = (): string =>
  JSON.stringify({ ...ONGOING_ASSESSMENT_REPLAY_CANONICAL, OrganizationId: '' });

export const cloneOngoingAssessmentReplay = (replay: OngoingAssessmentReplay): OngoingAssessmentReplay => clone(replay);

/** Local GET-only fixture. It has no API base class and no mutation method. */
export default class OngoingAssessmentReplayService {
  private readonly allowedOrganizationIds: readonly string[];

  constructor(allowedOrganizationIds: readonly string[] = CAPTURE_REPLAY_ORGANIZATION_IDS) {
    this.allowedOrganizationIds = Object.freeze([...allowedOrganizationIds]);
  }

  async Get(selectedOrganizationId: string, selectedOrganizationName: string, signal?: AbortSignal): Promise<OngoingAssessmentReplay> {
    if (signal?.aborted)
      throw new DOMException('The ongoing-assessment replay request was aborted.', 'AbortError');
    if (!isNorthstarCaptureReplayOrganization(selectedOrganizationId, selectedOrganizationName, this.allowedOrganizationIds))
      throw new Error('Ongoing-assessment replay is available only for the synthetic Northstar organization.');
    await Promise.resolve();
    if (signal?.aborted)
      throw new DOMException('The ongoing-assessment replay request was aborted.', 'AbortError');
    return clone({ ...ONGOING_ASSESSMENT_REPLAY_CANONICAL, OrganizationId: selectedOrganizationId });
  }
}
